import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AdminCategoryHome from "../../components/AdminSideComponents/AdminCategory/AdminCategoryHome";
import AdminSubCategoryHome from "../../components/AdminSideComponents/AdminSubCategory/AdminSubCategoryHome";
import { TbCategory2 } from "react-icons/tb";
import { FolderTree, Layers, Sparkles } from "lucide-react";

const AdminCategory = ({ defaultTab }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Determine initial tab from props, pathname, or URL query param
  const getInitialTab = () => {
    if (defaultTab) return defaultTab;
    const tabParam = searchParams.get("tab");
    if (tabParam === "subcategories" || tabParam === "subcategory") {
      return "subcategories";
    }
    if (location.pathname.includes("/admin-sub-categories")) {
      return "subcategories";
    }
    return "categories";
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [categoryCount, setCategoryCount] = useState(null);
  const [subCategoryCount, setSubCategoryCount] = useState(null);

  // Sync active tab if URL changes
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "subcategories" || tabParam === "subcategory") {
      setActiveTab("subcategories");
    } else if (tabParam === "categories") {
      setActiveTab("categories");
    } else if (location.pathname.includes("/admin-sub-categories")) {
      setActiveTab("subcategories");
    } else if (location.pathname.includes("/admin-categories")) {
      setActiveTab("categories");
    }
  }, [location.pathname, searchParams]);

  // Tab change handler
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === "subcategories") {
      navigate("/admin-sub-categories", { replace: true });
    } else {
      setSelectedCategoryFilter("all");
      navigate("/admin-categories", { replace: true });
    }
  };

  // When clicking a category's subcategory pill in AdminCategoryHome
  const handleSelectSubcategoriesOf = (categoryId) => {
    setSelectedCategoryFilter(categoryId);
    setActiveTab("subcategories");
    navigate("/admin-sub-categories", { replace: true });
  };

  return (
    <section className="min-h-screen bg-[#EEF3F7] p-4 sm:p-6 font-sans text-gray-800">
      {/* ─── MASTER HEADER ──────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row md:items-center justify-between pb-4 mb-4 border-b border-gray-200 gap-4">
        <div>
          <div className="flex items-center gap-2">

            <h1 className="text-2xl md:text-3xl font-philosopher font-bold text-gray-900 tracking-tight flex items-center gap-2">
              <TbCategory2 className="text-[#b07d17] w-8 h-8 inline-block" />
              Category & Subcategory Management
            </h1>
          </div>
          <p className="text-xs md:text-sm text-gray-600 mt-1">
            Dynamic catalog classifications, multilingual naming & sub-group hierarchy loaded live from database.
          </p>
        </div>

        {/* Live Counters */}
        <div className="flex items-center gap-2 self-start md:self-auto">
          {categoryCount !== null && (
            <span className="px-3 py-1 bg-white border border-gray-300 rounded-full text-xs font-medium shadow-2xs text-gray-700">
              Categories: <strong className="text-gray-900">{categoryCount}</strong>
            </span>
          )}
          {subCategoryCount !== null && (
            <span className="px-3 py-1 bg-white border border-gray-300 rounded-full text-xs font-medium shadow-2xs text-gray-700">
              Subcategories: <strong className="text-blue-700">{subCategoryCount}</strong>
            </span>
          )}
        </div>
      </div>

      {/* ─── DYNAMIC TAB SWITCHER STRIP ──────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-xs p-1.5 mb-5 flex items-center gap-2 max-w-md">
        {/* Tab 1: Categories */}
        <button
          type="button"
          onClick={() => handleTabChange("categories")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-xs font-bold transition-all duration-200 ${activeTab === "categories"
            ? "bg-[#b07d17] text-white shadow-sm ring-2 ring-[#b07d17]/30"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
        >
          <FolderTree className="w-4 h-4" />
          <span>All Categories</span>
          {categoryCount !== null && (
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${activeTab === "categories" ? "bg-black/20 text-white" : "bg-gray-200 text-gray-700"
                }`}
            >
              {categoryCount}
            </span>
          )}
        </button>

        {/* Tab 2: Subcategories */}
        <button
          type="button"
          onClick={() => handleTabChange("subcategories")}
          className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-xs font-bold transition-all duration-200 ${activeTab === "subcategories"
            ? "bg-[#b07d17] text-white shadow-sm ring-2 ring-[#b07d17]/30"
            : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            }`}
        >
          <Layers className="w-4 h-4" />
          <span>Subcategories</span>
          {subCategoryCount !== null && (
            <span
              className={`text-[10px] px-1.5 py-0.2 rounded-full font-mono font-bold ${activeTab === "subcategories" ? "bg-black/20 text-white" : "bg-gray-200 text-gray-700"
                }`}
            >
              {subCategoryCount}
            </span>
          )}
        </button>
      </div>

      {/* ─── TAB CONTENT PANELS ─────────────────────────────────────────── */}
      <div className="transition-all duration-300">
        {activeTab === "categories" ? (
          <AdminCategoryHome
            hideHeader={true}
            onSelectSubcategoriesOf={handleSelectSubcategoriesOf}
            onCategoryCountChange={setCategoryCount}
          />
        ) : (
          <AdminSubCategoryHome
            hideHeader={true}
            selectedCategoryId={selectedCategoryFilter}
            onClearCategoryFilter={() => setSelectedCategoryFilter("all")}
            onSubCategoryCountChange={setSubCategoryCount}
          />
        )}
      </div>
    </section>
  );
};

export default AdminCategory;